/**
 * Game picker. `SHELL-020`, `021`, `022`, `026`, `027`.
 *
 * Reads each game's name, description and level count from
 * `@platform/game-registry`'s `gameInfo` rather than loading the module --
 * `SHELL-027`. The registry entry is a hand-maintained copy of what
 * `GameModule` itself carries, kept honest by `game-registry.test.ts`'s
 * per-game loop rather than by construction; see that registry's own header
 * for why this trade is worth making.
 *
 * `KNOWN_GAME_IDS` is already the stable order `SHELL-026` asks for --
 * `Object.keys` on a literal object in `game-registry.ts`, which cannot
 * reorder itself between sessions or even between calls in the same one.
 * Nothing here sorts it; sorting it would be solving a problem this file does
 * not have.
 *
 * `SHELL-022`/`023`/`024` (a selected game's module loading, and what happens
 * if it fails) are level select's from here on: this screen never starts a
 * load, so it has nothing to show while one is in flight or if one fails. See
 * `LevelSelect.tsx`.
 */

import { useEffect, useRef, useState } from 'preact/hooks';

import { KNOWN_GAME_IDS, gameInfo, loadGame } from '@platform/game-registry';
import {
  cachedGameIdsSignal,
  isOnline,
  refreshCachedGameIds,
} from '@platform/offline-availability';
import { announce } from '@platform/announce';
import { completionSummary } from '@shell/progression';
import { routes, APP_NAME } from '@shell/routes';
import { currentDocument } from '@shell/save';
import { Screen } from '@shell/Screen';

export function Picker() {
  // SHELL-021. Read once, not per tile: `Boot.tsx` does not mount `App`, and
  // so cannot mount this screen, before `bootState` is `ready`.
  const document = currentDocument();
  const online = isOnline.value;
  const cached = cachedGameIdsSignal.value;
  // PWA-037. A game currently downloading, tracked separately from
  // `cached` -- `loadGame`'s own promise is what resolves this, and until it
  // does the tile has nothing new to report through `cachedGameIdsSignal`.
  const [downloading, setDownloading] = useState<Set<string>>(new Set());

  const handleDownload = (gameId: string): void => {
    const loading = loadGame(gameId);
    // Unreachable in practice: `gameId` is drawn from `KNOWN_GAME_IDS`,
    // which `loadGame` (`game-registry.ts`) always has an entry for.
    if (!loading) return;
    setDownloading((current) => new Set(current).add(gameId));
    void loading
      .then(async () => {
        // The service worker's own cache write can land a moment after the
        // import itself resolves -- `event.respondWith()` hands the page its
        // response before the paired `event.waitUntil()` cache write has
        // necessarily settled, so reading `cachedGameIdsSignal` exactly once,
        // immediately, is a real race: found by this control's own first
        // E2E run, which failed on exactly this gap. Polled briefly rather
        // than reading once and assuming the write already landed.
        for (let attempt = 0; attempt < 10; attempt += 1) {
          await refreshCachedGameIds();
          if (cachedGameIdsSignal.value.has(gameId)) return;
          await new Promise((resolve) => {
            setTimeout(resolve, 200);
          });
        }
      })
      .finally(() => {
        setDownloading((current) => {
          const next = new Set(current);
          next.delete(gameId);
          return next;
        });
      });
  };

  // PWA-032/033/036: cache status is read once per mount, not per tile --
  // one Cache Storage read for every game, not one per game.
  useEffect(() => {
    void refreshCachedGameIds();
  }, []);

  // A11Y-074: an offline-availability change is exactly the state change
  // platform/announce.ts exists for. `useRef`, not a dependency on `online`
  // alone, so the *first* render (mount) does not itself announce -- only a
  // real change while this screen is already open does.
  const previousOnline = useRef(online);
  useEffect(() => {
    if (previousOnline.current !== online) {
      announce(
        online
          ? 'Back online. All games are available.'
          : 'You are offline. Games not yet downloaded are unavailable.',
        'polite',
      );
    }
    previousOnline.current = online;
  }, [online]);

  return (
    <Screen title={APP_NAME}>
      <ul class="game-list">
        {KNOWN_GAME_IDS.map((gameId) => {
          const info = gameInfo(gameId);
          // Unreachable: `gameId` is drawn from the same registry `gameInfo`
          // reads. The check exists for the type checker, not a real branch.
          if (!info) return null;

          const summary = completionSummary(info.levelCount, document.games[gameId]);
          const isCached = cached.has(gameId);
          const isDownloading = downloading.has(gameId);

          return (
            <li key={gameId}>
              <a class="game-tile" href={routes.levelSelect(gameId)}>
                <span class="game-tile__name">{info.name}</span>
                <span class="game-tile__description">{info.description}</span>
                <span class="game-tile__progress">
                  {summary.completed} of {summary.total} levels complete
                </span>
                {/* PWA-036: shown whenever cached, independent of current
                    connectivity -- the requirement names no such condition. */}
                {isCached && <span class="game-tile__offline-status">Available offline</span>}
                {/* PWA-033: the picker telling the truth before the tap --
                    only shown offline, since it says nothing while online. */}
                {!isCached && !online && (
                  <span class="game-tile__offline-status">Unavailable offline</span>
                )}
              </a>
              {/* PWA-037: a real control to cache a game without playing it --
                  a <button>, not nested inside the <a> above (invalid,
                  interactive-in-interactive markup), a sibling instead.
                  Needs the network to mean anything, so only offered online;
                  hidden once cached, matching PWA-036's own badge above. */}
              {!isCached && online && (
                <button
                  type="button"
                  class="game-tile__download"
                  onClick={() => {
                    handleDownload(gameId);
                  }}
                  disabled={isDownloading}
                >
                  {isDownloading ? 'Downloading…' : `Download ${info.name} for offline`}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <nav class="screen-nav">
        <a class="nav-link" href={routes.settings()}>
          Settings
        </a>
      </nav>
    </Screen>
  );
}
